// src/components/AssessmentQuestion.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAssessmentContext } from '../contexts/AssessmentContext';
import HighlightedTTS from './HighlightedTTS'; 
import MathContent from './MathContent';
import SafeRichContent from './SafeRichContent';
import ReadingPassages from './ReadingPassages';
import InstructionAccess from './InstructionAccess';
import { resolveReadingPassages } from '../utils/readingPassages';
import WritingComposition from './WritingComposition';
import AdvancedQuestionRenderer from './AdvancedQuestionRenderer';
import OverallAssessmentTimer from './OverallAssessmentTimer';
import { api } from '../api/client';
import { isEnabledFlag } from '../utils/assessmentIdentity';
import { boundedMinimumSeconds, resolveQuestionMinimum } from '../utils/assessmentTiming';
import { moveOrderingItems } from '../utils/orderingInteraction';
import { subscribeToResponseSync } from '../utils/responseOutbox';
import { stopActiveSpeech } from '../utils/ttsRuntime';
import {
  readAssessmentInputDraft,
  resolveAssessmentInputValue,
} from '../utils/assessmentInputDraft';

// ==================== UTILITY FUNCTIONS ====================
const isAnimation = (url) => {
  if (!url) return false;
  return url.endsWith('.html') || url.startsWith('animation:');
};

const formatImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  const isAnim = imageUrl.endsWith('.html') || imageUrl.startsWith('animation:');
  
  if (isAnim) {
    let animationPath = imageUrl;
    if (imageUrl.startsWith('animation:')) {
      animationPath = imageUrl.substring(10).trim();
    }
    const normalizedPath = animationPath.startsWith('/') 
      ? animationPath.substring(1) 
      : animationPath;
    return `/api/animation/${normalizedPath}`;
  }
  
  if (imageUrl.startsWith('http')) {
    return imageUrl; 
  } else {
    const normalizedPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return normalizedPath;
  }
};

const getOptionText = (option) => {
  if (Array.isArray(option)) return option.map(item => getOptionText(item)).filter(Boolean).join('\n');
  if (option == null) return '';
  if (typeof option !== 'object') return String(option);
  return getOptionText(option.text);
};

const getOptionSpeech = (option) => {
  if (!option || typeof option === 'string') return '';
  return option.speech || option.MathSpeech || '';
};

// Delivery snapshots and imported assessments may represent interactive
// choices as CSV text, arrays, or the standard { text, speech } wrapper.
// Normalize all three without depending on answer-bearing fields.
const getDisplayItems = (value) => {
  const unwrapped = value && typeof value === 'object' && !Array.isArray(value)
    ? value.text
    : value;
  if (Array.isArray(unwrapped)) {
    return unwrapped
      .map(item => String(item?.text ?? item ?? '').trim())
      .filter(Boolean);
  }
  return String(unwrapped ?? '')
    .split(/\r?\n|\s*[,|]\s*/)
    .map(item => item.trim())
    .filter(Boolean);
};

const advancedQuestionTypes = new Set([
  'matching', 'dragAndDrop', 'hotspot',
  'multipleSelect', 'numericResponse', 'multiPart', 'evidencePair',
  'equationBuilder', 'numberLine', 'coordinateGraph', 'oralReading'
]);

const hasMeaningfulResponse = (value) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some(hasMeaningfulResponse);
  if (typeof value === 'object') return Object.values(value).some(hasMeaningfulResponse);
  const text = String(value).trim();
  if (!text) return false;
  if (text.startsWith('{') || text.startsWith('[')) {
    try { return hasMeaningfulResponse(JSON.parse(text)); } catch { return true; }
  }
  return true;
};

const validSavedOrdering = (authoredItems, savedValue) => {
  const savedItems = getDisplayItems(savedValue);
  const authoredCounts = authoredItems.reduce((counts, item) => {
    counts.set(item, (counts.get(item) || 0) + 1);
    return counts;
  }, new Map());
  const savedCounts = savedItems.reduce((counts, item) => {
    counts.set(item, (counts.get(item) || 0) + 1);
    return counts;
  }, new Map());
  return savedItems.length === authoredItems.length
    && authoredCounts.size === savedCounts.size
    && [...authoredCounts].every(([item, count]) => savedCounts.get(item) === count)
    ? savedItems
    : authoredItems;
};

function OrderingControl({ questionKey, authoredValue, savedValue, disabled, onOrderChange, ttsEnabled = false }) {
  const [items, setItems] = useState(() => validSavedOrdering(getDisplayItems(authoredValue), savedValue));
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    onOrderChange(items);
    // `key={questionKey}` remounts this control for another question/attempt.
    // Initialization must not be re-applied merely because its parent renders.
  }, [questionKey]);

  const move = (index, delta) => {
    if (disabled) return;
    setItems(previous => {
      const next = moveOrderingItems(previous, index, delta);
      if (next === previous) return previous;
      const destination = index + delta;
      onOrderChange(next);
      setAnnouncement(`${next[destination]} moved to position ${destination + 1}`);
      return next;
    });
  };

  return <>
    <div aria-live="polite" className="sr-only">{announcement}</div>
    <ol style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
      {items.map((item, index) => <li key={`${questionKey}-${item}-${index}`}
        className="assessment-order-item" style={{
          display: 'grid', gridTemplateColumns: '2rem 1fr auto auto', gap: '0.5rem',
          alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '0.5rem',
          padding: '0.65rem', marginBottom: '0.5rem', background: '#fff'
        }}>
        <strong>{index + 1}</strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', minWidth: 0 }}>
          <HighlightedTTS text={item} enabled={ttsEnabled} size="small" hideText ariaLabel={`Read ordering item ${index + 1} aloud`} />
          <MathContent text={item} />
        </div>
        <button type="button" aria-label={`Move ${item} up`} disabled={index === 0 || disabled}
          className="assessment-interaction-button" onClick={() => move(index, -1)}
          style={{ ...orderMoveButtonStyle, opacity: index === 0 || disabled ? 0.45 : 1 }}>↑</button>
        <button type="button" aria-label={`Move ${item} down`} disabled={index === items.length - 1 || disabled}
          className="assessment-interaction-button" onClick={() => move(index, 1)}
          style={{ ...orderMoveButtonStyle, opacity: index === items.length - 1 || disabled ? 0.45 : 1 }}>↓</button>
      </li>)}
    </ol>
  </>;
}

// ✅ FIXED: Always return the actual answer, let server validate
// This ensures:
// 1. Actual answers are stored in database (not '1' or '0')
// 2. Resume/batch validation works correctly
// 3. StudentResponses displays actual answers
const handleAnswerSubmission = (questionType, selectedAnswer, correctAnswer) => {
  // For all question types, return the actual answer
  // Server will handle validation via validateAnswerOnServer
  
  if (!selectedAnswer) {
    return '';
  }
  
  // For multiple choice and true/false, return as-is (A/B/C/D)
  if (questionType === 'multipleChoice' || questionType === 'trueFalse') {
    return selectedAnswer;
  }
  
  // For text-based inputs, normalize but return actual answer
  const trimmed = String(selectedAnswer).trim();
  
  // Normalize comma-separated inputs (ordering, dragAndDrop, matching)
  if (trimmed.includes(',')) {
    return trimmed
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .join(', ');
  }
  
  // For single-value inputs (fillInTheBlanks, hotspot, essay)
  return trimmed;
};

// ==================== TIMER COMPONENT ====================
const QuestionTimer = React.memo(({ 
  timerSeconds, 
  isActive, 
  onTimeUp, 
  questionIndex,
  isPaused = false,
  onTimerUpdate,
  initialActiveSeconds = 0,
  onCheckpoint,
  resetTrigger
}) => {
  const totalSeconds = boundedMinimumSeconds(timerSeconds);
  const restoredActiveSeconds = Math.min(
    totalSeconds,
    boundedMinimumSeconds(initialActiveSeconds)
  );
  const [activeSeconds, setActiveSeconds] = useState(restoredActiveSeconds);
  const [timeLeft, setTimeLeft] = useState(Math.max(0, totalSeconds - restoredActiveSeconds));
  const [isRunning, setIsRunning] = useState(false);
  const [hasExpired, setHasExpired] = useState(restoredActiveSeconds >= totalSeconds);
  const [isDocumentVisible, setIsDocumentVisible] = useState(
    typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  const intervalRef = useRef();
  const hasStartedRef = useRef(false);
  const activeSecondsRef = useRef(restoredActiveSeconds);
  const lastCheckpointRef = useRef(restoredActiveSeconds);
  const expiredRef = useRef(restoredActiveSeconds >= totalSeconds);

  const checkpoint = useCallback((force = false) => {
    const current = activeSecondsRef.current;
    if (!force && current === lastCheckpointRef.current) return;
    lastCheckpointRef.current = current;
    onCheckpoint?.(current)?.catch?.((error) => {
      console.warn('[QuestionTimer] Timing checkpoint delayed:', error);
    });
  }, [onCheckpoint]);

  // Reset timer when question changes or timer setting changes
  useEffect(() => {
    const restored = Math.min(totalSeconds, boundedMinimumSeconds(initialActiveSeconds));
    const expired = totalSeconds === 0 || restored >= totalSeconds;
    activeSecondsRef.current = restored;
    lastCheckpointRef.current = restored;
    expiredRef.current = expired;
    setActiveSeconds(restored);
    setTimeLeft(Math.max(0, totalSeconds - restored));
    setHasExpired(expired);
    hasStartedRef.current = false;

    const shouldRun = isActive && totalSeconds > 0 && !isPaused && !expired
      && (typeof document === 'undefined' || document.visibilityState === 'visible');
    setIsRunning(shouldRun);
    if (shouldRun) {
      hasStartedRef.current = true;
    }

    onTimerUpdate?.({
      timeLeft: Math.max(0, totalSeconds - restored),
      activeSeconds: restored,
      isRunning: shouldRun,
      hasExpired: expired
    });
  }, [questionIndex, totalSeconds, resetTrigger]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      setIsDocumentVisible(visible);
      if (!visible) checkpoint(true);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkpoint]);

  // Separate effect for pause/resume logic
  useEffect(() => {
    if (totalSeconds <= 0) return;
    const shouldRun = isActive && !isPaused && !hasExpired && isDocumentVisible;
    if (shouldRun) hasStartedRef.current = true;
    if (shouldRun !== isRunning) {
      setIsRunning(shouldRun);
      onTimerUpdate?.({ timeLeft, activeSeconds, isRunning: shouldRun, hasExpired });
    }
  }, [isPaused, isActive, hasExpired, isDocumentVisible, timeLeft, activeSeconds, isRunning, totalSeconds, onTimerUpdate]);

  // Timer countdown effect
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        setActiveSeconds(previousActive => {
          const nextActive = Math.min(totalSeconds, previousActive + 1);
          const newTimeLeft = Math.max(0, totalSeconds - nextActive);
          activeSecondsRef.current = nextActive;
          setTimeLeft(newTimeLeft);
          onTimerUpdate?.({
            timeLeft: newTimeLeft,
            activeSeconds: nextActive,
            isRunning: newTimeLeft > 0,
            hasExpired: newTimeLeft <= 0
          });
          if (nextActive > 0 && nextActive % 10 === 0) checkpoint();
          if (newTimeLeft <= 0 && !expiredRef.current) {
            expiredRef.current = true;
            setIsRunning(false);
            setHasExpired(true);
            checkpoint(true);
            onTimeUp?.();
          }
          return nextActive;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, timeLeft, totalSeconds, checkpoint, onTimeUp, onTimerUpdate]);

  useEffect(() => () => checkpoint(true), [checkpoint, questionIndex]);

  if (totalSeconds <= 0) return null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    const percentage = (timeLeft / totalSeconds) * 100;
    if (percentage <= 10) return '#ef4444'; // Red
    if (percentage <= 25) return '#f59e0b'; // Orange
    return '#10b981'; // Green
  };

  const getTimerIcon = () => {
    if (hasExpired) return '✅';
    if (!isRunning) return '⏸️';
    if (timeLeft <= 10) return '⚠️';
    return '⏱️';
  };

  const getTimerLabel = () => {
    if (hasExpired) return 'Minimum Complete';
    if (!isRunning) return 'Minimum Paused';
    return 'Minimum Time';
  };

  return (
    <div 
      style={{
        ...timerContainerStyle,
        borderLeft: `3px solid ${getTimerColor()}`,
        backgroundColor: hasExpired 
          ? 'rgba(16, 185, 129, 0.1)' 
          : timeLeft <= 10 
            ? 'rgba(239, 68, 68, 0.1)' 
            : 'rgba(15, 23, 42, 0.05)'
      }}
      data-timer-expired={hasExpired}
    >
      <div style={timerIconStyle}>
        {getTimerIcon()}
      </div>
      <div style={timerContentStyle}>
        <div style={{
          ...timerDisplayStyle,
          color: hasExpired ? '#10b981' : getTimerColor(),
          fontWeight: timeLeft <= 10 || hasExpired ? 'bold' : 'normal'
        }}>
          {formatTime(timeLeft)}
        </div>
        <div style={timerLabelStyle}>
          {getTimerLabel()}
        </div>
      </div>
    </div>
  );
});

// ==================== COMPONENTS ====================
const ImageOrAnimation = React.memo(({ src, alt, style }) => {
  const [hasError, setHasError] = useState(false);
  const formattedSrc = formatImageUrl(src);
  
  const handleError = useCallback(() => {
    console.error('Media failed to load:', src);
    setHasError(true);
  }, [src]);

  const handleLoad = useCallback((e) => {
    if (isAnimation(src)) {
      try {
        const iframeContent = e.target.contentWindow.document.body.innerHTML;
        if (iframeContent.includes('error') || iframeContent.includes('not found')) {
          console.error('Animation loaded with error content:', src);
          setHasError(true);
        }
      } catch (err) {
        // Ignore cross-origin errors
      }
    }
  }, [src]);

  if (hasError || !src) return null;
  
  if (isAnimation(src)) {
    return (
      <iframe
        src={formattedSrc}
        title={alt || "HTML5 Animation"}
        style={{
          width: "100%", 
          height: "300px", 
          border: "none",
          overflow: "hidden",
          ...style 
        }}
        sandbox="allow-scripts allow-same-origin"
        loading="lazy"
        onError={handleError}
        onLoad={handleLoad}
      />
    );
  }
  
  return (
    <img
      src={formattedSrc}
      alt={alt || "Visual content"}
      style={style}
      onError={handleError}
    />
  );
});

const OptionContent = React.memo(({ option }) => {
  if (typeof option === 'string') {
    return option;
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {option.text && <span>{option.text}</span>}
      {option.image && (
        <ImageOrAnimation
          src={option.image}
          alt="Option visual"
          style={{ maxWidth: '100px', height: 'auto', objectFit: 'contain' }}
        />
      )}
    </div>
  );
});

const DebugPanel = React.memo(({ currentQuestion, currentQuestionIndex, studentResponses, timerSeconds, furthestReached, shouldTimerBeActive, timerState, settingsLoaded, assessmentType, accessCode }) => {
  if (process.env.NODE_ENV !== 'development') return null;
  
  return (
    <div style={debugPanelStyle}>
      <div><strong>Debug Info:</strong></div>
      <div>Question: {currentQuestion?.ItemNumber}</div>
      <div>Index: {currentQuestionIndex}</div>
      <div>Response: {studentResponses[currentQuestion?.ItemNumber] || 'None'}</div>
      <div>Total Responses: {Object.keys(studentResponses).length}</div>
      <div style={{ color: '#00ff00' }}>Timer (Context): {timerSeconds}s</div>
      <div>Furthest: {furthestReached}</div>
      <div>SettingsLoaded: {settingsLoaded ? 'Yes' : 'No'}</div>
      <div>ShouldTimerBeActive: {shouldTimerBeActive ? 'Yes' : 'No'}</div>
      <div>TimerRunning: {timerState.isRunning ? 'Yes' : 'No'}</div>
      <div>TimerExpired: {timerState.hasExpired ? 'Yes' : 'No'}</div>
      <div>AssessmentType: {assessmentType}</div>
      <div>AccessCode: {accessCode}</div>
    </div>
  );
});

// ==================== MAIN COMPONENT ====================
export default function AssessmentQuestion() {
  const {
    assessmentData,
    currentQuestionIndex,
    studentResponses,
    correctAnswers,
    recordAnswer,
    handleWritingAnswer,
    writingResults,
    writingDrafts,
    itemTiming,
    saveAssessmentItemState,
    currentPerformanceId,
    navigateQuestion,
    finalizeAssessment,
    assessmentKind,
    totalQuestions,
    accessCode,
    assessmentType,
    selectedAssessment,
    sessionContinueCode,
    inputStates,
    setInputStates,
    timerSettings, // ✅ GET TIMER SETTINGS FROM CONTEXT
  } = useAssessmentContext();

  // Timer/review progress belongs to one durable session. A global browser
  // key could otherwise make a new or resumed student look as though they were
  // reviewing an older student's later question, which pauses the timer.
  const furthestStorageKey = sessionContinueCode
    ? `furthestQuestionReached:${sessionContinueCode}`
    : 'furthestQuestionReached';

  // ==================== STATE ====================
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overallLimitReached, setOverallLimitReached] = useState(
    Boolean(timerSettings?.overallTiming?.expired)
  );
  const [writingScored, setWritingScored] = useState({}); // Track which writing questions have been scored
  const [orderingState, setOrderingState] = useState({});
  const [orderingConfirmed, setOrderingConfirmed] = useState({});
  const [advancedDrafts, setAdvancedDrafts] = useState({});
  const [writingSubmission, setWritingSubmission] = useState(null);
  const [navigationPhase, setNavigationPhase] = useState('');
  const [responseSync, setResponseSync] = useState({ state: 'ready', pending: 0, message: '' });
  // ✅ REMOVED: Local timer state - now using context
  // const [timerSeconds, setTimerSeconds] = useState(null);
  // const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [timerState, setTimerState] = useState({ timeLeft: 0, isRunning: false, hasExpired: false });
  const [timerResetTrigger, setTimerResetTrigger] = useState(0);
  
  // Initialize furthestQuestionReached properly
  const [furthestQuestionReached, setFurthestQuestionReached] = useState(() => {
    const saved = Number.parseInt(localStorage.getItem(furthestStorageKey), 10);
    const current = Number.parseInt(currentQuestionIndex, 10) || 1;
    return Number.isInteger(saved) && saved > 0 ? Math.max(saved, current) : current;
  });

  // ✅ USE TIMER SETTINGS FROM CONTEXT
  const timerSeconds = timerSettings?.timerSeconds;
  const settingsLoaded = timerSettings?.settingsLoaded;
  const overallTiming = timerSettings?.overallTiming;
  const interactionLocked = isSubmitting || overallLimitReached;
  // AssessmentLogin receives this exact user's assigned-and-visible catalog
  // row from the server. Reuse its TTS flag so rendering never waits on a
  // second settings request and the SQLite string "0" cannot become truthy.
  const ttsEnabled = isEnabledFlag(selectedAssessment?.tts_enabled);
  const displayProfile = selectedAssessment?.display_profile_snapshot
    || selectedAssessment?.display_profile
    || 'classic';
  const readingWorkspace = displayProfile === 'reading_workspace';
 
  // ==================== REFS ====================
  const prevQuestionRef = useRef();
  const submitTimeoutRef = useRef();
  const actionInFlightRef = useRef(false);

  useEffect(() => {
    if (!currentPerformanceId) return undefined;
    const update = detail => {
      if (detail.performanceId != null && Number(detail.performanceId) !== Number(currentPerformanceId)) return;
      setResponseSync(previous => ({ ...previous, ...detail }));
    };
    const unsubscribe = subscribeToResponseSync(update);
    api.flushResponseOutbox(currentPerformanceId).catch(() => undefined);
    return unsubscribe;
  }, [currentPerformanceId]);
  
  // ==================== CONSTANTS ====================
  const isResumedSession = !!sessionContinueCode;
  const numericTypes = useMemo(() => 
    ['ordering', 'fillInTheBlanks'], 
    []
  );
  const orderingKeyFor = useCallback((questionId) => {
    const assessmentIdentity = selectedAssessment?.id
      || selectedAssessment?.assessmentId
      || assessmentType
      || 'assessment';
    const attemptIdentity = sessionContinueCode || currentPerformanceId || 'attempt';
    return `${assessmentIdentity}:${attemptIdentity}:${String(questionId)}`;
  }, [selectedAssessment?.id, selectedAssessment?.assessmentId, assessmentType, sessionContinueCode, currentPerformanceId]);

  // ==================== COMPUTED VALUES ====================
  const { currentGrade, currentQuestion, currentItemGradeIndex, currentItemQuestionIndex } = useMemo(() => {
    if (!assessmentData) return { currentGrade: null, currentQuestion: null, currentItemGradeIndex: 0, currentItemQuestionIndex: 0 };
    
    let grade, question, itemGradeIndex = 0, itemQuestionIndex = 0;
    if (assessmentKind === 'regular') {
      grade = assessmentData.Grades[0];
      itemQuestionIndex = Math.max(0, currentQuestionIndex - 1);
      question = grade?.Questions[itemQuestionIndex];
    } else {
      // The global question position is the single display authority. Derive
      // its grade block instead of trusting a second state value that can lag
      // during resume.
      let remainingIndex = Math.max(0, currentQuestionIndex - 1);
      for (const [candidateGradeIndex, candidateGrade] of assessmentData.Grades.entries()) {
        const candidateQuestions = candidateGrade?.Questions || [];
        if (remainingIndex < candidateQuestions.length) {
          grade = candidateGrade;
          itemGradeIndex = candidateGradeIndex;
          itemQuestionIndex = remainingIndex;
          question = candidateQuestions[remainingIndex];
          break;
        }
        remainingIndex -= candidateQuestions.length;
      }
    }
    
    return { currentGrade: grade, currentQuestion: question,
      currentItemGradeIndex: itemGradeIndex, currentItemQuestionIndex: itemQuestionIndex };
  }, [assessmentData, assessmentKind, currentQuestionIndex]);

  const effectiveTimerSeconds = useMemo(() => resolveQuestionMinimum(
    timerSettings?.timingPolicy,
    currentQuestion,
    timerSeconds
  ), [timerSettings?.timingPolicy, currentQuestion, timerSeconds]);
  const currentItemTiming = itemTiming?.[String(currentQuestion?.ItemNumber)] || null;
  const initialActiveSeconds = boundedMinimumSeconds(currentItemTiming?.activeSeconds);

  const questionData = useMemo(() => {
    if (!currentQuestion || !currentGrade) return {};
    
    return {
      direction: assessmentKind === 'regular' ? currentQuestion.Direction : currentGrade.Direction,
      directionSpeech: assessmentKind === 'regular'
        ? currentQuestion.DirectionSpeech
        : currentGrade.DirectionSpeech,
      readingPassage: assessmentKind === 'regular' ? currentQuestion.ReadingPassage : currentGrade.ReadingPassage,
      readingPassageSpeech: assessmentKind === 'regular'
        ? currentQuestion.ReadingPassageSpeech
        : currentGrade.ReadingPassageSpeech,
      readingPassageImage: assessmentKind === 'regular' ? currentQuestion.Image : currentGrade.Image,
      readingPassageOwner: assessmentKind === 'regular' ? currentQuestion : currentGrade,
      questionText: currentQuestion.Question,
      questionSpeech: currentQuestion.QuestionSpeech,
      questionImage: assessmentKind === 'regular' ? currentQuestion.image : currentGrade.image,
    };
  }, [currentQuestion, currentGrade, assessmentKind]);

  const shouldShowPooledBadge = useMemo(() => {
    return assessmentKind === 'regular' &&
      ((assessmentData?.assessmentPool === 'Pooled') ||
       (assessmentData?.pool === 'Pooled') ||
       (selectedAssessment?.assessmentPool === 'Pooled'));
  }, [assessmentKind, assessmentData, selectedAssessment]);

  // ✅ FIXED: Use timer settings from context
  const shouldTimerBeActive = useMemo(() => {
    // ✅ CRITICAL: Don't activate timer if settings haven't loaded or timer is disabled
    if (!settingsLoaded || effectiveTimerSeconds <= 0) {

      return false;
    }
    
    const isReviewingPrevious = currentQuestionIndex < furthestQuestionReached;
    const shouldActivate = !isReviewingPrevious;
        
    return shouldActivate;
  }, [settingsLoaded, effectiveTimerSeconds, currentQuestionIndex, furthestQuestionReached]);

  // FIXED: Timer should be paused only when reviewing previous questions
  const shouldTimerBePaused = useMemo(() => {
    const isPaused = currentQuestionIndex < furthestQuestionReached;

    return isPaused;
  }, [currentQuestionIndex, furthestQuestionReached]);

  // ==================== EFFECTS ====================
  
  // Clear old data on fresh assessments
  useEffect(() => {
    if (!sessionContinueCode && currentQuestionIndex === 1) {
      setFurthestQuestionReached(1);
      localStorage.setItem(furthestStorageKey, '1');
    }
  }, [sessionContinueCode, currentQuestionIndex, furthestStorageKey]);

  // Rehydrate when a different durable session is opened in the same browser.
  useEffect(() => {
    const saved = Number.parseInt(localStorage.getItem(furthestStorageKey), 10);
    const current = Number.parseInt(currentQuestionIndex, 10) || 1;
    const restored = Number.isInteger(saved) && saved > 0
      ? Math.max(saved, current)
      : current;
    setFurthestQuestionReached(restored);
    localStorage.setItem(furthestStorageKey, String(restored));
  }, [furthestStorageKey]);

  // A reused tablet may open several attempts in one browser process. Locks
  // and unsaved structured drafts must belong only to the active performance.
  useEffect(() => {
    setOverallLimitReached(Boolean(overallTiming?.expired));
    setAdvancedDrafts({});
  }, [currentPerformanceId]);

  // Clear old assessment data on mount
  useEffect(() => {
    const clearOldAssessmentData = () => {
      const assessmentKeys = [
        'furthestQuestionReached',
        'failedBlocksCount',
        'assessmentProgress',
        'userAnswers',
        'assessmentSession',
        'currentQuestionIndex',
        'timerState',
        'assessmentStartTime'
      ];
      
      const urlParams = new URLSearchParams(window.location.search);
      const forceClear = urlParams.get('clearData') === 'true';
      const shouldClearData = currentQuestionIndex === 1 || forceClear;
      
      if (shouldClearData) {
        assessmentKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
          }
        });
        setFurthestQuestionReached(1);
        localStorage.setItem(furthestStorageKey, '1');
      }
    };
    
    clearOldAssessmentData();
  }, []);

  // Update furthest question reached
  useEffect(() => {
    if (currentQuestionIndex > furthestQuestionReached) {
      setFurthestQuestionReached(currentQuestionIndex);
      localStorage.setItem(furthestStorageKey, currentQuestionIndex.toString());
    }
  }, [currentQuestionIndex, furthestQuestionReached, furthestStorageKey]);

  // ✅ UPDATE: Log timer state from context
  useEffect(() => {

  }, [effectiveTimerSeconds, settingsLoaded, shouldTimerBeActive, currentQuestionIndex, furthestQuestionReached, timerSettings]);

  // ✅ UPDATE: Force timer refresh when context timer settings change
  useEffect(() => {
    if (settingsLoaded && effectiveTimerSeconds !== null) {

      setTimerResetTrigger(prev => prev + 1);
    }
  }, [settingsLoaded, effectiveTimerSeconds]);

  // FIXED: Reset timer only when question actually changes
  useEffect(() => {
    const currentQuestionId = currentQuestion?.ItemNumber;
    const prevQuestionId = prevQuestionRef.current?.ItemNumber;
    
    if (currentQuestionId !== prevQuestionId && currentQuestion) {
      stopActiveSpeech('item-changed');
      
      prevQuestionRef.current = currentQuestion;
      
      // Force timer reset by incrementing trigger
      setTimerResetTrigger(prev => prev + 1);
    }
  }, [currentQuestion?.ItemNumber, currentQuestionIndex]);

  // Ordering questions restore the accepted response on resume/Back. This keeps
  // the visible order and the answer that Next will persist identical.
  useEffect(() => {
    const type = currentQuestion?.questionType || currentQuestion?.QuestionType;
    if (type !== 'ordering' || currentQuestion?.ItemNumber == null) return;
    const key = orderingKeyFor(currentQuestion.ItemNumber);
    setOrderingState(previous => {
      if (Array.isArray(previous[key]) && previous[key].length > 0) return previous;
      const authoredItems = getDisplayItems(currentQuestion?.Options?.Items);
      const items = validSavedOrdering(authoredItems, studentResponses[String(currentQuestion.ItemNumber)]);
      return { ...previous, [key]: items };
    });
  }, [currentQuestion, orderingKeyFor, studentResponses]);

  // Cleanup effects
  useEffect(() => {
    const cleanup = injectMediaQueries();
    return cleanup;
  }, []);

  useEffect(() => {
    return () => {
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }
    };
  }, []);

  // ==================== HANDLERS ====================
  
  // Updated handleTimerExpired function - remove auto-selection
  const handleTimerExpired = useCallback(() => {
    
    if (!currentQuestion || isSubmitting) return;
    
    // ✅ REMOVED: Auto-selection logic
    // Just log that timer expired - Next button will be enabled via getButtonDisabledState
  }, [currentQuestion, isSubmitting]);

  const getDisplayValue = useCallback((questionType, storedValue, questionId) => {
    if (numericTypes.includes(questionType)) {
      return resolveAssessmentInputValue({ inputStates, questionId, storedValue });
    }
    return storedValue ?? '';
  }, [numericTypes, inputStates]);

  const handleInputChange = useCallback((questionId, value) => {
    if (overallLimitReached) return;
    setInputStates(prev => ({
      ...prev,
      [questionId]: value,
    }));
  }, [setInputStates, overallLimitReached]);

  const handleInputSubmit = useCallback((e) => {
    // Enter no longer creates a second, hidden submission path. Students use
    // the same visible Next transaction for every question type.
    if (e.key === 'Enter') e.preventDefault();
  }, []);

  const moveOrderingItem = useCallback((questionId, index, delta, fallbackItems = []) => {
    if (overallLimitReached) return;
    const key = orderingKeyFor(questionId);
    setOrderingState(previous => {
      const items = [...((previous[key]?.length ? previous[key] : fallbackItems) || [])];
      const destination = index + delta;
      if (destination < 0 || destination >= items.length) return previous;
      [items[index], items[destination]] = [items[destination], items[index]];
      return { ...previous, [key]: items };
    });
    setOrderingConfirmed(previous => ({ ...previous, [key]: false }));
  }, [overallLimitReached, orderingKeyFor]);

  const confirmOrderingAnswer = useCallback(async (question) => {
    const key = orderingKeyFor(question.ItemNumber);
    const items = orderingState[key]?.length
      ? orderingState[key]
      : getDisplayItems(question?.Options?.Items);
    if (!items.length || interactionLocked) return;
    const answer = items.join(', ');
    setIsSubmitting(true);
    try {
      setInputStates(previous => ({ ...previous, [question.ItemNumber]: answer }));
      await recordAnswer(
        question.ItemNumber,
        answer,
        question.questionType || question.QuestionType || 'ordering',
        question.CorrectAnswer
      );
      setOrderingConfirmed(previous => ({ ...previous, [key]: true }));
    } finally {
      setIsSubmitting(false);
    }
  }, [orderingState, interactionLocked, orderingKeyFor, recordAnswer, setInputStates]);

  // FIXED: Option selection no longer resets timer
  const handleOptionSelect = useCallback((optionKey) => {
    if (interactionLocked) return;
    setInputStates(previous => ({ ...previous, [currentQuestion.ItemNumber]: optionKey }));
  }, [currentQuestion, interactionLocked, setInputStates]);

  // ✅ Wrapper for writing answer that sets local scored state immediately
  const handleWritingAnswerWithLocalState = useCallback(async (questionId, text, questionType, rubricResult) => {
    const qKey = String(questionId);
    if (!handleWritingAnswer) throw new Error('Writing response handler is unavailable');
    const result = await handleWritingAnswer(questionId, text, questionType, rubricResult);
    setWritingScored(prev => ({ ...prev, [qKey]: true, [questionId]: true }));
    return result;
  }, [handleWritingAnswer]);

  const checkpointCurrentItem = useCallback((activeSeconds) => {
    if (!currentQuestion?.ItemNumber || !currentPerformanceId || !saveAssessmentItemState) {
      return Promise.resolve(null);
    }
    return saveAssessmentItemState(currentQuestion.ItemNumber, { activeSeconds });
  }, [currentQuestion?.ItemNumber, currentPerformanceId, saveAssessmentItemState]);

  const checkpointWritingDraft = useCallback((draftText) => {
    if (!currentQuestion?.ItemNumber || !currentPerformanceId || !saveAssessmentItemState) {
      return Promise.resolve(null);
    }
    return saveAssessmentItemState(currentQuestion.ItemNumber, { draftText });
  }, [currentQuestion?.ItemNumber, currentPerformanceId, saveAssessmentItemState]);

  const captureAdvancedDraft = useCallback((questionId, value, complete = false) => {
    const key = String(questionId);
    setAdvancedDrafts(previous => {
      if (!hasMeaningfulResponse(value)) {
        if (!Object.hasOwn(previous, key)) return previous;
        const updated = { ...previous };
        delete updated[key];
        return updated;
      }
      if (previous[key]?.answer === value && previous[key]?.complete === Boolean(complete)) return previous;
      return { ...previous, [key]: { answer: value, complete: Boolean(complete) } };
    });
  }, []);

  const handleOverallTimeExpired = useCallback(async () => {
    if (!currentPerformanceId || !currentQuestion) {
      throw new Error('The timed assessment has no active performance record');
    }

    setOverallLimitReached(true);
    setIsSubmitting(true);
    try {
      const questionNumber = currentQuestion.ItemNumber;
      const questionKey = String(questionNumber);
      const questionType = currentQuestion.questionType || currentQuestion.QuestionType || '';
      const storedResponse = studentResponses[questionNumber] ?? studentResponses[questionKey];
      let answer;

      if (questionType === 'writingComposition') {
        const draftStorageKey = `assessnow-writing-draft:${sessionContinueCode || currentPerformanceId || 'active'}:${questionKey}`;
        let localDraft = '';
        try { localDraft = window.sessionStorage.getItem(draftStorageKey) || ''; } catch { /* server draft fallback */ }
        const serverDraft = writingDrafts?.[questionKey]?.text || '';
        // Do not replace an already scored writing response unless the student
        // actually edited it and created a new unfinished draft.
        answer = localDraft || serverDraft || undefined;
      } else if (advancedQuestionTypes.has(questionType)) {
        answer = advancedDrafts[questionKey]?.answer ?? storedResponse;
      } else if (questionType === 'ordering') {
        const items = orderingState[orderingKeyFor(questionNumber)] || [];
        answer = items.length > 0 ? items.join(', ') : storedResponse;
      } else if (numericTypes.includes(questionType)) {
        const draft = readAssessmentInputDraft(inputStates, questionNumber);
        answer = draft.present
          ? (hasMeaningfulResponse(draft.value)
              ? handleAnswerSubmission(questionType, draft.value, currentQuestion.CorrectAnswer)
              : undefined)
          : storedResponse;
      } else if (questionType === 'multipleChoice' || questionType === 'trueFalse') {
        const selected = typeof document === 'undefined'
          ? null
          : document.querySelector(`input[name="question-${questionNumber}"]:checked`);
        answer = selected?.value ?? storedResponse;
      } else {
        answer = storedResponse;
      }

      const finalResponse = hasMeaningfulResponse(answer)
        ? { questionNumber, answer: String(answer) }
        : null;
      await finalizeAssessment({ reason: 'time_expired', finalResponse });
    } catch (error) {
      // Keep all response controls locked, but allow the timer component to
      // retry this idempotent server operation when connectivity returns.
      setIsSubmitting(false);
      throw error;
    }
  }, [
    currentPerformanceId,
    currentQuestion,
    studentResponses,
    sessionContinueCode,
    writingDrafts,
    advancedDrafts,
    orderingState,
    orderingKeyFor,
    numericTypes,
    inputStates,
    finalizeAssessment,
  ]);

  const getRadioCheckedState = useCallback((questionId, optionKey) => {
    const draftResponse = inputStates[questionId] ?? inputStates[String(questionId)];
    if (draftResponse !== undefined && draftResponse !== null) return draftResponse === optionKey;
    const storedResponse = studentResponses[questionId];
    return storedResponse !== undefined && storedResponse !== null 
      ? storedResponse === optionKey 
      : false;
  }, [studentResponses, inputStates]);

  // ✅ Updated getButtonDisabledState function
  const getButtonDisabledState = useCallback(() => {
    if (!currentQuestion) return true;
    
    const questionId = currentQuestion.ItemNumber;
    const qKey = String(questionId);
    const storedResponse = studentResponses[questionId] || studentResponses[qKey];
    const qType = currentQuestion.questionType || currentQuestion.QuestionType || '';

    if (interactionLocked) return true;

    // The configured value is a minimum time on every new question, including
    // writing and advanced structured types. Review navigation stays unlocked.
    if (shouldTimerBeActive && effectiveTimerSeconds > 0 && !shouldTimerBePaused && !timerState.hasExpired) {
      return true;
    }
    
    if (qType === 'writingComposition') {
      return !writingSubmission?.ready;
    }

    if (advancedQuestionTypes.has(qType)) {
      return !advancedDrafts[qKey]?.complete;
    }
    
    if (numericTypes.includes(qType)) {
      if (qType === 'ordering') {
        const items = orderingState[orderingKeyFor(questionId)] || [];
        return items.length === 0;
      }
      const draft = readAssessmentInputDraft(inputStates, questionId);
      return draft.present
        ? !hasMeaningfulResponse(draft.value)
        : !hasMeaningfulResponse(storedResponse);
    }

    // Timer has expired, not active, or reviewing previous question - use normal logic
    if (storedResponse !== undefined && storedResponse !== null && storedResponse !== '') {
      return false;
    }
    
    if (qType === 'multipleChoice' || qType === 'trueFalse') {
      return !hasMeaningfulResponse(inputStates[questionId] ?? inputStates[qKey] ?? storedResponse);
    }
    
    return true;
  }, [currentQuestion, studentResponses, writingSubmission, advancedDrafts, orderingState, orderingKeyFor, inputStates, numericTypes, interactionLocked, shouldTimerBeActive, shouldTimerBePaused, timerState.hasExpired, effectiveTimerSeconds]);
    
  const shouldTerminateGradeLevelAt = useCallback((questionIndex, questionId, answerIsCorrect) => {
    if (assessmentKind !== 'gradeLevel' || !assessmentData?.Grades?.length) return false;

    // Derive block boundaries from the immutable definition. This keeps
    // resumed attempts deterministic even when browser-local state is empty.
    let cumulativeQuestions = 0;
    const blockEnds = assessmentData.Grades.map(grade => {
      cumulativeQuestions += (grade.Questions || []).length;
      return cumulativeQuestions;
    });
    const currentBlockIndex = blockEnds.indexOf(questionIndex);
    if (currentBlockIndex < 0) return false;

    let consecutiveFailedBlocks = 0;
    for (let blockIndex = 0; blockIndex <= currentBlockIndex; blockIndex += 1) {
      const blockQuestions = assessmentData.Grades[blockIndex]?.Questions || [];
      const correctCount = blockQuestions.reduce((count, question) => {
        const itemNumber = String(question.ItemNumber);
        const isCorrect = itemNumber === String(questionId)
          ? answerIsCorrect === true
          : correctAnswers[itemNumber] === true;
        return count + (isCorrect ? 1 : 0);
      }, 0);
      consecutiveFailedBlocks = correctCount >= 3
        ? 0
        : consecutiveFailedBlocks + 1;
    }

    return consecutiveFailedBlocks >= 3
      || currentBlockIndex >= assessmentData.Grades.length - 1;
  }, [assessmentKind, assessmentData, correctAnswers]);

  // Next is the only student commit boundary. It validates the on-screen
  // draft, saves/scores it, durably advances the cursor, and never moves when
  // any preceding step fails.
  const handleNextOrComplete = useCallback(async () => {
    if (interactionLocked || actionInFlightRef.current) return;
    stopActiveSpeech(currentQuestionIndex >= totalQuestions ? 'assessment-submitting' : 'item-navigation');
    actionInFlightRef.current = true;
    try {
      const allQuestions = assessmentData?.Grades?.flatMap(grade => grade.Questions || []) || [];
      const question = allQuestions[currentQuestionIndex - 1];
      if (!question) throw new Error('The current question is unavailable');
      if (shouldTimerBeActive && !shouldTimerBePaused && !timerState.hasExpired) return;

      const questionId = question.ItemNumber;
      const questionKey = String(questionId);
      const questionType = question.questionType || question.QuestionType || '';
      const storedResponse = studentResponses[questionKey];
      let answer = storedResponse;
      let answerIsCorrect = correctAnswers[questionKey] === true;

      setIsSubmitting(true);
      setNavigationPhase(questionType === 'writingComposition' ? 'scoring' : 'saving');
      try {
        if (questionType === 'writingComposition') {
          if (!writingSubmission?.ready || typeof writingSubmission.submit !== 'function') {
            throw new Error('Complete the writing requirement before continuing');
          }
          const rubric = await writingSubmission.submit();
          answerIsCorrect = Number(rubric?.percentage || 0) >= Number(question.PassingThreshold || 60);
        } else {
          if (advancedQuestionTypes.has(questionType)) {
            const structured = advancedDrafts[questionKey];
            if (!structured?.complete) throw new Error('Complete every required part before continuing');
            answer = structured.answer;
          } else if (questionType === 'ordering') {
            const items = orderingState[orderingKeyFor(questionId)] || [];
            if (!items.length) throw new Error('This ordering item has no choices');
            answer = items.join(', ');
          } else {
            answer = inputStates[questionId] ?? inputStates[questionKey] ?? storedResponse;
          }
          if (!hasMeaningfulResponse(answer)) throw new Error('Select or enter an answer before continuing');

          const correctAnswer = questionType === 'fillInTheBlanks' && !question.CorrectAnswer
            ? question.Options?.Answers
            : question.CorrectAnswer;
          answerIsCorrect = await recordAnswer(
            questionId,
            handleAnswerSubmission(questionType, answer, correctAnswer),
            questionType,
            correctAnswer
          );
        }

        const shouldTerminate = shouldTerminateGradeLevelAt(
          currentQuestionIndex,
          questionId,
          answerIsCorrect
        );
        if (shouldTerminate || currentQuestionIndex >= totalQuestions) {
          localStorage.removeItem(furthestStorageKey);
          setNavigationPhase('submitting');
          await finalizeAssessment();
          return;
        }

        setNavigationPhase('moving');
        await navigateQuestion('next', { answeredItemNumber: questionId });
        setFurthestQuestionReached(previous => {
          const next = Math.max(previous, currentQuestionIndex + 1);
          localStorage.setItem(furthestStorageKey, String(next));
          return next;
        });
      } catch (error) {
        console.error('Answer was not submitted; remaining on the current item:', error);
        alert(`Your answer could not be saved. Your work is still displayed. ${error.message || 'Please select Next again.'}`);
      } finally {
        setNavigationPhase('');
        setIsSubmitting(false);
      }
    } finally {
      actionInFlightRef.current = false;
    }
  }, [
    interactionLocked, assessmentData, currentQuestionIndex, studentResponses, inputStates,
    writingSubmission, advancedDrafts, orderingState, orderingKeyFor,
    assessmentKind, correctAnswers, totalQuestions, sessionContinueCode,
    recordAnswer, navigateQuestion, finalizeAssessment, shouldTerminateGradeLevelAt,
    shouldTimerBeActive, shouldTimerBePaused, timerState.hasExpired, numericTypes,
    furthestStorageKey
  ]);

  const handleBackNavigation = useCallback(() => {
    if (interactionLocked) return;
    stopActiveSpeech('item-navigation');
    navigateQuestion('back').catch(error => {
      console.error('Back navigation failed:', error);
    });
  }, [navigateQuestion, interactionLocked]);

  // ==================== RENDER FUNCTIONS ====================
  const renderInputField = useCallback(({ type, questionId, placeholder }) => {
    const storedResponse = studentResponses[questionId];
    const currentValue = getDisplayValue(type, storedResponse, questionId);
    const isDisabled = false;
    
    return (
      <input
        type="text"
        value={currentValue}
        onChange={(e) => handleInputChange(questionId, e.target.value)}
        onKeyDown={handleInputSubmit}
        placeholder={placeholder}
        style={largeInputStyle}
        disabled={isDisabled || interactionLocked}
        className="assessment-input"
      />
    );
  }, [studentResponses, getDisplayValue, numericTypes, handleInputChange, handleInputSubmit, interactionLocked]);

  const renderAnswerSection = useCallback(() => {
    if (!currentQuestion) return null;

    const currentType = currentQuestion.questionType || currentQuestion.QuestionType;
    if (advancedQuestionTypes.has(currentType)) {
      return (
        <AdvancedQuestionRenderer
          key={`advanced-${currentQuestion.ItemNumber}`}
          question={currentQuestion}
          storedResponse={studentResponses[String(currentQuestion.ItemNumber)] || ''}
          disabled={interactionLocked}
          ttsEnabled={ttsEnabled}
          onDraftChange={captureAdvancedDraft}
          onCommit={async (answer) => {
            await recordAnswer(
              currentQuestion.ItemNumber,
              answer,
              currentType,
              currentQuestion.CorrectAnswer
            );
          }}
        />
      );
    }

    switch (currentType) {
      case 'multipleChoice':
        return (
          <div>
            {Object.entries(currentQuestion.Options).map(([key, option]) => {
              const isChecked = getRadioCheckedState(currentQuestion.ItemNumber, key);
              
              return (
                <label
                  key={key}
                  style={optionLabelStyle}
                  className={`assessment-option-label assessment-choice${isChecked ? ' assessment-choice-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion.ItemNumber}`}
                    value={key}
                    checked={isChecked}
                    onChange={() => handleOptionSelect(key)}
                    style={optionInputStyle}
                    disabled={interactionLocked}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                    <HighlightedTTS
                      text={`${key}. ${getOptionText(option)}`}
                      speechText={getOptionSpeech(option) ? `${key}. ${getOptionSpeech(option)}` : ''}
                      enabled={ttsEnabled}
                      size="small"
                      preserveLineBreaks={false}
                      containerStyle={{}}
                      textStyle={{ fontSize: '1.5rem', color: '#2d3748' }}
                      highlightedStyle={{ backgroundColor: 'yellow' }}
                    />
                    {typeof option !== 'string' && option.image && (
                      <ImageOrAnimation
                        src={formatImageUrl(option.image)}
                        alt="Option visual"
                        style={{ maxWidth: '100px', height: 'auto', objectFit: 'contain' }}
                      />
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        );

      case 'trueFalse':
        return (
          <div>
            {Object.entries(currentQuestion.Options).map(([key, option]) => {
              const isChecked = getRadioCheckedState(currentQuestion.ItemNumber, key);
              
              return (
                <label
                  key={key}
                  style={optionLabelStyle}
                  className={`assessment-option-label assessment-choice${isChecked ? ' assessment-choice-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion.ItemNumber}`}
                    value={key}
                    checked={isChecked}
                    onChange={() => handleOptionSelect(key)}
                    style={optionInputStyle}
                    disabled={interactionLocked}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                    <HighlightedTTS
                      text={getOptionText(option)}
                      speechText={getOptionSpeech(option)}
                      enabled={ttsEnabled}
                      size="small"
                      containerStyle={{}}
                      textStyle={{ fontSize: '1.5rem', color: '#2d3748' }}
                      highlightedStyle={{ backgroundColor: 'yellow' }}
                    />
                  </div>
                </label>
              );
            })}
          </div>
        );

      case 'ordering':
        {
        const questionKey = orderingKeyFor(currentQuestion.ItemNumber);
        const authoredItems = getDisplayItems(currentQuestion?.Options?.Items);
        return (
          <div>
            <p style={{ ...inputLabelStyle, marginTop: 0 }}>
              Put the items in the correct order. Use the arrow buttons to move each item.
            </p>
            {authoredItems.length > 0 && <OrderingControl
              key={questionKey}
              questionKey={questionKey}
              authoredValue={authoredItems}
              savedValue={studentResponses[String(currentQuestion.ItemNumber)]}
              disabled={interactionLocked}
              ttsEnabled={ttsEnabled}
              onOrderChange={nextItems => setOrderingState(previous => ({ ...previous, [questionKey]: nextItems }))}
            />}
            {!authoredItems.length && (
              <p role="alert" style={{ color: '#b91c1c', fontWeight: 700 }}>
                This ordering question has no choices. Please return to the assessment list and notify your teacher.
              </p>
            )}
          </div>
        );
        }

      case 'fillInTheBlanks':
        return (
          <div>
            <div style={inputLabelStyle}>
              <HighlightedTTS
                text="Type your answer:"
                enabled={ttsEnabled}
                size="small"
                containerStyle={{}}
                textStyle={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: '#2d3748',
                  cursor: 'pointer',
                }}
                highlightedStyle={{ backgroundColor: 'yellow' }}
              />
            </div>
            {renderInputField({
              type: 'fillInTheBlanks',
              questionId: currentQuestion.ItemNumber,
              placeholder: 'Type your answer here',
            })}
          </div>
        );

      // ✅ NEW: Writing composition question type (360-IF scored)
      case 'writingComposition':
        {
          const questionKey = String(currentQuestion.ItemNumber);
          const serverDraft = writingDrafts?.[questionKey]?.text || '';
          const minimumTimeComplete = effectiveTimerSeconds <= 0
            || shouldTimerBePaused
            || timerState.hasExpired;
        return (
          <WritingComposition
            key={`writing-${currentQuestion.ItemNumber}`}
            question={assessmentKind === 'gradeLevel'
              ? { ...currentQuestion, ReadingPassages: currentGrade?.ReadingPassages, ReadingPassage: currentGrade?.ReadingPassage }
              : currentQuestion}
            ttsEnabled={ttsEnabled}
            onAnswer={handleWritingAnswerWithLocalState}
            onDraftChange={checkpointWritingDraft}
            isSubmitting={interactionLocked}
            studentResponse={studentResponses[String(currentQuestion.ItemNumber)] || ''}
            draftText={serverDraft}
            draftStorageKey={`assessnow-writing-draft:${sessionContinueCode || currentPerformanceId || 'active'}:${questionKey}`}
            minimumTimeComplete={minimumTimeComplete}
            minimumSeconds={effectiveTimerSeconds}
            registerSubmission={setWritingSubmission}
            HighlightedTTS={HighlightedTTS}
          />
        );
        }

      case 'multipleSelect':
      case 'numericResponse':
      case 'multiPart':
      case 'evidencePair':
      case 'equationBuilder':
      case 'numberLine':
      case 'coordinateGraph':
      case 'oralReading':
        return (
          <AdvancedQuestionRenderer
            key={`advanced-${currentQuestion.ItemNumber}`}
            question={currentQuestion}
            storedResponse={studentResponses[String(currentQuestion.ItemNumber)] || ''}
            disabled={interactionLocked}
            ttsEnabled={ttsEnabled}
            onDraftChange={captureAdvancedDraft}
            onCommit={async (answer) => {
              await recordAnswer(
                currentQuestion.ItemNumber,
                answer,
                currentQuestion.questionType || currentQuestion.QuestionType,
                currentQuestion.CorrectAnswer
              );
            }}
          />
        );

      default:
        return (
          <div>
            {Object.entries(currentQuestion.Options).map(([key, option]) => {
              const isChecked = getRadioCheckedState(currentQuestion.ItemNumber, key);
              
              return (
                <label
                  key={key}
                  style={optionLabelStyle}
                  className="assessment-option-label"
                  onMouseEnter={(e) => {
                    if (!interactionLocked) {
                      e.currentTarget.style.backgroundColor = '#f0f4ff';
                      e.currentTarget.style.transform = 'scale(1.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion.ItemNumber}`}
                    value={key}
                    checked={isChecked}
                    onChange={() => handleOptionSelect(key)}
                    style={optionInputStyle}
                    disabled={interactionLocked}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                    <HighlightedTTS
                      text={`${key}. ${getOptionText(option)}`}
                      speechText={getOptionSpeech(option) ? `${key}. ${getOptionSpeech(option)}` : ''}
                      enabled={ttsEnabled}
                      size="small"
                      containerStyle={{}}
                      textStyle={{ fontSize: '1.5rem', color: '#2d3748' }}
                      highlightedStyle={{ backgroundColor: 'yellow' }}
                    />
                  </div>
                </label>
              );
            })}
          </div>
        );
    }
  }, [
    currentQuestion, 
    getRadioCheckedState, 
    handleOptionSelect, 
    handleWritingAnswer,
    handleWritingAnswerWithLocalState,
    checkpointWritingDraft,
    writingDrafts,
    sessionContinueCode,
    currentPerformanceId,
    effectiveTimerSeconds,
    shouldTimerBePaused,
    timerState.hasExpired,
    studentResponses,
    ttsEnabled, 
    renderInputField,
    interactionLocked,
    captureAdvancedDraft
  ]);

  // ==================== EARLY RETURNS ====================
  if (!assessmentData || !currentQuestion) return null;

  // ==================== MAIN RENDER ====================
  return (
    <div style={containerStyle} className={`assessment-container${readingWorkspace ? ' assessment-reading-workspace' : ''}`} data-display-profile={displayProfile}>
      <DebugPanel 
        currentQuestion={currentQuestion}
        currentQuestionIndex={currentQuestionIndex}
        studentResponses={studentResponses}
        timerSeconds={effectiveTimerSeconds}
        furthestReached={furthestQuestionReached}
        shouldTimerBeActive={shouldTimerBeActive}
        timerState={timerState}
        settingsLoaded={settingsLoaded}
        assessmentType={assessmentType}
        accessCode={accessCode}
      />
      
      <div style={cardStyle} className="assessment-card">
        {/* Header */}
        <div style={headerStyle}>
          <div className="assessment-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 360px', minWidth: 0 }}>
              <HighlightedTTS
                text={questionData.direction}
                speechText={questionData.directionSpeech}
                enabled={ttsEnabled}
                size="small"
                preserveLineBreaks={false}
                containerStyle={{ margin: 0, padding: 0, maxWidth: '100%' }}
                textStyle={{ ...headerTitleStyle, className: "assessment-header-title" }}
                highlightedStyle={{ backgroundColor: 'yellow' }}
              />
            </div>
            <InstructionAccess variant="toolbar" phase="during"
              key={`lesson-help:${currentPerformanceId}:${currentItemGradeIndex}:${currentItemQuestionIndex}:${currentQuestion.ItemNumber}`}
              assessmentId={selectedAssessment?.id}
              performanceId={currentPerformanceId} itemNumber={String(currentQuestion.ItemNumber)}
              gradeIndex={currentItemGradeIndex} questionIndex={currentItemQuestionIndex}
              deliveryIndex={Math.max(0, currentQuestionIndex - 1)} />
          </div>
        </div>

        <div style={{ ...mainContentStyle, ...(readingWorkspace ? { gap: '1.25rem', alignItems: 'stretch' } : {}) }} className="assessment-main-content">
          {/* Left Column = Reading Passage */}
          {(resolveReadingPassages(questionData.readingPassageOwner).length || !readingWorkspace) && <div style={{ ...leftColumnStyle, ...(readingWorkspace ? { flex: '1.15 1 54%', background: 'transparent', border: 0, borderRadius: '12px', padding: 0 } : {}) }} className="assessment-left-column">
            {shouldShowPooledBadge && (
              <div style={readingWorkspace ? { ...pooledBadgeStyle, position: 'static', width: 'fit-content', marginLeft: 'auto', marginBottom: 10, fontSize: '.75rem', padding: '5px 9px' } : pooledBadgeStyle} aria-label="Pooled Assessment">
                PA
              </div>
            )}
            
            
            <ReadingPassages key={`${sessionContinueCode || currentPerformanceId || 'preview'}:${currentItemGradeIndex}:${currentItemQuestionIndex}:${currentQuestion?.ItemNumber}`} owner={questionData.readingPassageOwner} ttsEnabled={ttsEnabled} textSize={readingWorkspace ? '1.45rem' : '1.85rem'} scopeKey={`${sessionContinueCode || currentPerformanceId || 'preview'}:${currentItemGradeIndex}:${currentItemQuestionIndex}:${currentQuestion?.ItemNumber}`} />
          </div>}

          {/* Right Column = Question + Answers */}
          <div key={currentQuestion.ItemNumber} style={{ ...rightColumnStyle, ...(readingWorkspace ? { flex: resolveReadingPassages(questionData.readingPassageOwner).length ? '.85 1 46%' : '1 1 100%', background: '#ffffff', border: '1px solid #d8dee8', borderRadius: '12px', padding: 'clamp(18px, 2.2vw, 28px)', boxShadow: '0 8px 24px rgba(15,23,42,.06)' } : {}) }} className="assessment-right-column assessment-question-stage">
            {['error', 'protected', 'reconnecting'].includes(responseSync.state) && <div role="status" aria-live="polite" style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, alignSelf: 'flex-end',
              marginBottom: 8, padding: '6px 10px', borderRadius: 999,
              fontSize: '.78rem', fontWeight: 700,
              color: responseSync.state === 'error' ? '#991b1b' : '#9a3412',
              background: responseSync.state === 'error' ? '#fee2e2' : '#ffedd5',
              border: `1px solid ${responseSync.state === 'error' ? '#fecaca' : '#fed7aa'}`
            }}>
              <span aria-hidden="true">●</span>
              {responseSync.state === 'reconnecting' ? 'Reconnecting…'
                : responseSync.state === 'protected' ? 'Connection interrupted — answer protected'
                  : 'Answer needs attention'}
            </div>}
            <div style={questionHeaderStyle}>
              <div style={questionIndexStyle}>Question {currentQuestionIndex}</div>
              
              {/* ✅ Timer Display - Only show if timer is enabled */}
              <div style={timerCenterContainerStyle}>
                <OverallAssessmentTimer
                  performanceId={currentPerformanceId}
                  timing={overallTiming}
                  onExpire={handleOverallTimeExpired}
                />
                {effectiveTimerSeconds > 0 && (
                  <QuestionTimer
                    timerSeconds={effectiveTimerSeconds}
                    isActive={shouldTimerBeActive}
                    onTimeUp={handleTimerExpired}
                    questionIndex={currentQuestionIndex}
                    isPaused={shouldTimerBePaused || overallLimitReached}
                    onTimerUpdate={setTimerState}
                    initialActiveSeconds={initialActiveSeconds}
                    onCheckpoint={checkpointCurrentItem}
                    resetTrigger={timerResetTrigger}
                  />
                )}
              </div>
              
              <div style={questionCountContainerStyle}>
                <div style={questionCountStyle}>
                  Question {currentQuestionIndex} of {totalQuestions}
                </div>
              </div>
            </div>

            {questionData.questionImage && (
              <div style={{ marginBottom: '1rem' }}>
                <ImageOrAnimation
                  src={formatImageUrl(questionData.questionImage)}
                  alt="Question visual"
                  style={{ maxWidth: '100%', marginBottom: '1rem', borderRadius: '0.5rem' }}
                />
              </div>
            )}

            <div 
              style={questionCardStyle}
              onContextMenu={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            >
              <HighlightedTTS
                text={questionData.questionText}
                speechText={questionData.questionSpeech}
                enabled={ttsEnabled}
                size="medium"
                preserveLineBreaks={false}
                containerStyle={{}}
                textStyle={{ ...questionTextStyle, className: "assessment-question-text" }}
                highlightedStyle={{ backgroundColor: 'yellow' }}
              />
            </div>

            <div>{renderAnswerSection()}</div>

            <div style={navContainerStyle} className="assessment-nav-container">
              <button
                onClick={handleBackNavigation}
                disabled={currentQuestionIndex <= 1 || interactionLocked}
                style={navButtonStyle(currentQuestionIndex <= 1 || interactionLocked)}
                className="assessment-nav-button assessment-interaction-button"
              >
                Back
              </button>
              <button
                onClick={handleNextOrComplete}
                disabled={getButtonDisabledState()}
                style={{
                  ...navButtonStyle(getButtonDisabledState()),
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginRight: currentQuestionIndex >= totalQuestions ? '10px' : 0,
                }}
                className={`assessment-nav-button assessment-interaction-button${currentQuestionIndex >= totalQuestions ? ' assessment-submit-button' : ''}${getButtonDisabledState() ? ' assessment-next-disabled' : ''}`}
              >
                {navigationPhase === 'saving' ? 'Saving…'
                  : navigationPhase === 'scoring' ? 'Scoring…'
                    : navigationPhase === 'moving' ? 'Opening next item…'
                      : navigationPhase === 'submitting' ? 'Submitting assessment…'
                        : (currentQuestionIndex >= totalQuestions ? 'Submit Assessment' : 'Next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== STYLES ====================
const styles = {
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
  },
};

// Timer styles
const timerContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  borderRadius: '8px',
  backgroundColor: 'rgba(15, 23, 42, 0.05)',
  border: '1px solid rgba(148, 163, 184, 0.2)',
  fontSize: '12px',
  minWidth: '120px',
  justifyContent: 'flex-start',
};

const timerIconStyle = {
  fontSize: '14px',
  flexShrink: 0,
};

const timerContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '2px',
};

const timerDisplayStyle = {
  fontSize: '16px',
  fontWeight: '600',
  fontFamily: 'monospace',
  lineHeight: 1,
};

const timerLabelStyle = {
  fontSize: '10px',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  lineHeight: 1,
};

// Debug panel style
const debugPanelStyle = {
  position: 'fixed',
  top: '10px',
  right: '10px',
  background: 'rgba(0,0,0,0.8)',
  color: 'white',
  padding: '10px',
  borderRadius: '5px',
  fontSize: '12px',
  zIndex: 1000,
  maxWidth: '300px'
};

// Main container styles
const containerStyle = {
  padding: '0',
  minHeight: '100vh',
  background: 'rgba(49, 74, 234, 0.5)',
  width: '100%',
  maxWidth: '100vw',
  boxSizing: 'border-box',
  overflow: 'hidden',
  margin: 0,
};

const cardStyle = {
  backgroundColor: '#fff',
  borderRadius: '1rem',
  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
  minHeight: '100vh',
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  padding: '1.5rem',
  borderBottom: '1px solid #e2e8f0',
  backgroundColor: '#f7fafc',
};

const headerTitleStyle = {
  fontSize: '1.75rem',
  fontWeight: '700',
  color: '#2d3748',
  margin: 0,
};

const mainContentStyle = {
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  overflow: 'hidden',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
  }
};

const leftColumnStyle = {
  width: '50%',
  padding: '2rem',
  borderRight: '1px solid #e2e8f0',
  backgroundColor: '#fff',
  overflowY: 'auto',
  overflowX: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  maxHeight: 'calc(100vh - 120px)',
  scrollBehavior: 'smooth',
  '@media (max-width: 768px)': {
    width: '100%',
    maxHeight: '40vh',
    borderRight: 'none',
    borderBottom: '1px solid #e2e8f0',
    height: 'auto',
  }
};

const rightColumnStyle = {
  width: '50%',
  padding: '2rem',
  backgroundColor: '#fafafa',
  overflowY: 'auto',
  position: 'relative',
  '@media (max-width: 768px)': {
    width: '100%',
    flex: 1,
  }
};

const questionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.5rem',
  marginBottom: '1.25rem',
};

const questionIndexStyle = {
  fontSize: '1.25rem',
  color: '#4a5568',
  flex: '0 0 auto',
};

const timerCenterContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.5rem',
  flex: '1',
};

const questionCountContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  flex: '0 0 auto',
};

const questionCountStyle = {
  fontSize: '1.25rem',
  color: '#718096',
};

const questionCardStyle = {
  backgroundColor: '#fff3e0',
  padding: '2rem',
  marginBottom: '2rem',
  borderRadius: '0.75rem',
  border: '1px solid #ffe0b2',
  // Prevent text selection and copying
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  WebkitTouchCallout: 'none',
};

const questionTextStyle = {
  fontSize: '1.8rem',
  fontWeight: '700',
  color: '#212121',
  lineHeight: '1.4',
  margin: 0,
};

const inputLabelStyle = {
  fontSize: '1.5rem',
  fontWeight: '600',
  color: '#2d3748',
  display: 'block',
  marginBottom: '0.75rem',
};

const largeInputStyle = {
  ...styles.input,
  fontSize: '1.5rem',
  padding: '1rem',
  marginTop: '0.5rem',
  maxWidth: '400px',
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: '0.5rem',
  border: '1px solid #ccc',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  ':focus': {
    borderColor: '#2563eb',
    boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    outline: 'none'
  }
};

const orderMoveButtonStyle = {
  minWidth: '3rem',
  minHeight: '3rem',
  border: 'none',
  borderRadius: '0.5rem',
  background: '#2563eb',
  color: '#fff',
  fontSize: '1.25rem',
  cursor: 'pointer'
};

const navButtonStyle = (disabled) => ({
  padding: '1rem 2rem',
  backgroundColor: disabled ? '#9ca3af' : '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '0.5rem',
  fontSize: '1.25rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  transition: 'all 0.2s ease',
  width: '100px',
  textAlign: 'center',
  ':hover': !disabled ? {
    backgroundColor: '#1d4ed8',
    transform: 'translateY(-1px)',
  } : {}
});

const navContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '2.5rem',
  '@media (max-width: 768px)': {
    marginTop: '1.5rem',
  }
};

const passageCardStyle = {
  padding: '2rem',
  borderRadius: '0.75rem',
  border: '1px solid #e2e8f0',
  backgroundColor: '#f0f8ff',
  flex: 1,
  overflowY: 'auto',
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  // Prevent copying
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
};

const optionLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '1.25rem',
  marginBottom: '1.25rem',
  cursor: 'pointer',
  backgroundColor: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '0.75rem',
  transition: 'all 0.2s ease',
};

const optionInputStyle = {
  width: '1.5rem',
  height: '1.5rem',
  marginRight: '1rem',
};

const pooledBadgeStyle = {
  position: 'absolute',
  top: '10px',
  right: '10px',
  backgroundColor: '#e53e3e',
  color: 'white',
  padding: '0.5rem 1rem',
  borderRadius: '0.5rem',
  fontWeight: 'bold',
  zIndex: 10,
};

// Media queries injection function
const injectMediaQueries = () => {
  const style = document.createElement('style');
  style.innerHTML = `
    #root {
      max-width: 100vw;
      overflow-x: hidden;
    }
    body {
      overflow-x: hidden;
    }
    .assessment-reading-workspace .assessment-main-content {
      padding: clamp(10px, 1.5vw, 20px);
      background: #eef2f6;
    }
    .assessment-reading-workspace .assessment-passage-card p {
      margin: 0 0 1em;
    }
    @media (max-width: 768px) {
      body {
        overflow: auto !important;
      }
      .assessment-card {
        height: auto !important;
        min-height: 100vh !important;
        position: relative !important;
        overflow: visible !important;
      }
      .assessment-main-content {
        flex-direction: column !important;
        height: auto !important;
        overflow: visible !important;
      }
      .assessment-left-column {
        width: 100% !important;
        height: auto !important;
        max-height: 40vh !important;
        border-right: none !important;
        border-bottom: 1px solid #e2e8f0 !important;
        padding-left: 1rem !important;
        padding-right: 1rem !important;
        overflow-y: auto !important;
      }
      .assessment-reading-workspace .assessment-left-column {
        max-height: none !important;
        flex: none !important;
      }
      .assessment-right-column {
        width: 100% !important;
        height: auto !important;
        flex: none !important;
        padding-left: 1rem !important;
        padding-right: 1rem !important;
        box-sizing: border-box !important;
        overflow: visible !important;
      }
      .assessment-matching-box {
        flex-direction: column !important;
      }
      .assessment-nav-button {
        padding: 0.75rem 1rem !important;
        font-size: 1rem !important;
        width: 100px !important; 
        max-width: 100px !important;
      }
      .assessment-container {
        padding: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        overflow-x: hidden !important;
        overflow-y: auto !important;
        height: auto !important;
        position: relative !important;
      }
      .assessment-header-title {
        font-size: 1.5rem !important;
      }
      .assessment-question-text {
        font-size: 1.6rem !important;
      }
        
      .assessment-option-label {
        padding: 1rem 0.75rem !important;
        width: 100% !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
      }
      .assessment-input {
        max-width: 100% !important;
        width: 100% !important;
      }
      .assessment-nav-container {
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
        justify-content: space-between !important;
        width: 100% !important;
        box-sizing: border-box !important;
        padding-bottom: 2rem !important;
      }
    }
    @media (max-width: 576px) {
      .assessment-header-row {
        align-items: stretch !important;
      }
      .assessment-lesson-help {
        width: 100% !important;
        align-items: stretch !important;
      }
      .assessment-lesson-help > button {
        width: 100% !important;
      }
      .assessment-nav-container {
        margin-top: 1rem !important;
        width: 100% !important;
        display: flex !important;
        justify-content: space-between !important;
        box-sizing: border-box !important;
      }
      .assessment-nav-button {
        padding: 0.5rem 0.75rem !important;
        font-size: 0.875rem !important;
        width: 80px !important;
        max-width: 80px !important;
      }
      .assessment-header-title {
        font-size: 1.25rem !important;
      }
      .assessment-question-text {
        font-size: 1.4rem !important;
      }
      .assessment-container {
        width: 100vw !important;
        max-width: 100vw !important;
        overflow-x: hidden !important;
      }
      .assessment-card {
        width: 100% !important;
        max-width: 100% !important;
      }
    }
    @media screen and (max-width: 428px) {
      .assessment-container {
        padding: 0 !important;
        margin: 0 !important;
        width: 100vw !important;
        overflow-x: hidden !important;
        overflow-y: auto !important;
      }
      .assessment-card {
        width: 100% !important;
        margin: 0 !important;
        border-radius: 0 !important;
      }
      .assessment-left-column, 
      .assessment-right-column {
        padding-left: 12px !important;
        padding-right: 12px !important;
      }
      .assessment-option-label {
        max-width: 100% !important;
        width: 100% !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
      }
      .assessment-nav-container {
        padding-left: 12px !important;
        padding-right: 12px !important;
        width: calc(100% - 24px) !important;
      }
    }

    /* Custom scrollbar styles */
    .assessment-left-column::-webkit-scrollbar {
      width: 8px;
    }
    
    .assessment-left-column::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    
    .assessment-left-column::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
    }
    
    .assessment-left-column::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }

    /* Ensure passage content scrolls properly */
    .assessment-passage-card {
      overflow-y: auto;
      max-height: 100%;
      /* Prevent copying */
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    }
    
    .assessment-passage-card * {
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    }

    /* Prevent copying question text */
    .assessment-question-text {
      user-select: none !important;
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      -webkit-touch-callout: none !important;
    }

    .assessment-question-text * {
      user-select: none !important;
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
    }

    /* Focus styles for accessibility */
    .assessment-input:focus {
      border-color: #2563eb !important;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1) !important;
      outline: none !important;
    }

    /* Button hover effects */
    .assessment-nav-button:hover:not(:disabled) {
      background-color: #1d4ed8 !important;
      transform: translateY(-1px) !important;
    }

    .assessment-nav-button.assessment-submit-button {
      width: auto !important;
      max-width: none !important;
      min-width: 11rem !important;
      padding-left: 1.25rem !important;
      padding-right: 1.25rem !important;
      white-space: nowrap !important;
      line-height: 1.2 !important;
    }

    .assessment-nav-button:active:not(:disabled) {
      transform: translateY(0) !important;
    }

    /* Loading state for buttons */
    .assessment-nav-button:disabled {
      cursor: not-allowed !important;
      opacity: 0.6 !important;
    }
  `;
  document.head.appendChild(style);
  return () => {
    if (document.head.contains(style)) {
      document.head.removeChild(style);
    }
  };
};
